
import { Component, Provide, Mixins, Prop } from "vue-property-decorator";
import { CreateElement } from "vue";

// Mixins
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";

// Data/Enums
import { DataModules } from "@/store/modules/data";

// Types
import {
  ISelectMappingProviderData,
  ISelectMappingProviderMethods
} from "@/models/providers/selectMapping";
import { ISorter, IFilter } from "@/models/tables";
import { Sorters } from "@/data/table";

@Component
export default class SelectMappingProvider extends Mixins(
  FilteringSortingTableStore
) {
  // Props

  @Prop({ default: `SMLP` /** [S]elect [M]apping [L]ist [P]rovider */ })
  public declare id: string;

  @Prop({ default: false })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleGuarding: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  @Prop({ default: () => ({}) })
  public readonly manualFilters!: Record<string, any>;

  @Prop({ default: 12 })
  public declare limit: number;

  // Mapping params

  @Prop({ required: true })
  public availableFilters!: () => IFilter[];

  @Prop({ required: true })
  public availableSorters!: ISorter[];

  @Prop({ required: true })
  public usedDataModule!: DataModules;

  @Prop({ default: () => ({}) })
  public storeParams!: Function;

  @Prop({
    default: () => () => ({
      field: "created_at",
      direction: Sorters.DESCENDING
    })
  })
  public initialSorter!: Function;

  @Prop({ default: () => [] })
  public guardFunctionalities!: string[];

  @Prop({ default: () => () => Promise.resolve() })
  public loadExtraData!: Function;

  @Prop({ default: () => () => Promise.resolve() })
  public destroyExtraData!: Function;

  // Data

  public isLoading = true;
  public isReloading = false;

  public debounceSearch = this.$_.debounce(this.doQuickSearch, 250);
  public searchQuery = "";

  // Life-cycle Methods

  public created() {
    if (!this.contentIsAvailable) return;

    this.$set(this, "dataModule", this.usedDataModule);
    this.loadData();
  }

  public beforeDestroy() {
    this.destroyExtraData(this);
  }

  // Methods

  public async loadData() {
    try {
      await this.loadExtraData(this);
      this.filters = await this.availableFilters();

      this.defaults.sort = this.initialSorter();

      await this.getData();
      this.requestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public refreshData() {
    this.getData({ ignoreStored: true });
  }

  public async reloadData() {
    try {
      this.isReloading = true;
      await this.cancelRequests();
      await this.refreshData();
    } finally {
      this.isReloading = false;
    }
  }

  public getStoreParams(payload: any): Promise<any> {
    return this.$_.merge(payload, {
      storeModule: this.dataModule,
      ignoreStored: true,
      params: this.storeParams(this.searchQuery)
    });
  }

  public doQuickSearch(value = "") {
    if (value === this.searchQuery) return Promise.resolve();
    this.searchQuery = value;
    return this.reloadData();
  }

  // Guarding

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Provide

  @Provide("selectMappingProviderData")
  public providerData(): ISelectMappingProviderData {
    return {
      activeFilters: this.activeFilters,
      activeSort: this.table.sort,
      availableFilters: this.filters,
      availableSorters: this.availableSorters,
      enableFiltering: this.enableFiltering,
      enableSorting: this.enableSorting,
      guardFunctionalities: this.guardFunctionalities,
      isLoading: this.table.loading || this.isLoading,
      isReloading: this.isReloading,
      items: this.table.data || [],
      limit: this.table.limit,
      page: this.table.page,
      searchQuery: this.searchQuery,
      table: this.table,
      total: this.table.total
    };
  }

  @Provide("selectMappingProviderMethods")
  public get providerMethods(): ISelectMappingProviderMethods {
    return {
      debounceSearch: this.debounceSearch,
      doQuickSearch: this.doQuickSearch,
      getData: this.getData,
      onFiltersChange: this.onFiltersChange,
      onLimitChange: this.onLimitChange,
      onPageChange: this.onPageChange,
      onSort: this.onSort,
      reloadData: this.reloadData
    };
  }

  // Render

  public render(h: CreateElement) {
    // Handle guarding
    if (this.handleGuarding && !this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle loading
    if (this.handleLoading && this.loading)
      return h("is-loading", { props: { if: true } });

    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: this.reloadData }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $selectMappingData: this.providerData(),
        $selectMappingMethods: this.providerMethods
      })
    );
  }
}
